import { DeletedTextRun, InsertedTextRun, PageNumber, TextRun } from 'docx';
import type { NodeEntry } from 'slate';

import type { DocumentContext } from './document-context';
import { getEditDate, surroundTextWithComments } from './internal';
import type { DOCXInline } from './text';
import { createRunProperties } from './text';
import { assertNeverThrow } from '../../..';
import type { InlineInstructionFieldElement } from '../../../slate-api';
import {
  isInsertInstructionFieldEdit,
  isRemoveInstructionFieldEdit,
  Queries,
} from '../../../slate-api';

function getInlineInstructionFieldChildren(instructionField: InlineInstructionFieldElement) {
  switch (instructionField.instruction) {
    case 'PAGE':
      return [PageNumber.CURRENT];
    case 'NUMPAGES':
      return [PageNumber.TOTAL_PAGES];
    case 'REF':
    case 'PAGEREF':
    case 'NOTEREF':
      return [];
    default:
      assertNeverThrow(
        instructionField.instruction,
        `Unknown instruction field: ${instructionField.instruction}`
      );
  }
}

export function createInlineInstructionField(
  instructionFieldEntry: NodeEntry<InlineInstructionFieldElement>,
  options: { context: DocumentContext }
): DOCXInline[] {
  const [instructionField, instructionFieldPath] = instructionFieldEntry;
  const { context } = options;
  const textNode = instructionField.children[0];
  const runProperties = createRunProperties(Queries.getActiveTextProps(textNode));
  const suggestedInsertEdit = instructionField.suggestedEdits?.find(isInsertInstructionFieldEdit);
  const suggestedDeleteEdit = instructionField.suggestedEdits?.find(isRemoveInstructionFieldEdit);

  if (suggestedInsertEdit) {
    const insertedTextRun = new InsertedTextRun({
      ...runProperties,
      author: context.getAuthorName(suggestedInsertEdit.authorId),
      children: getInlineInstructionFieldChildren(instructionField),
      date: getEditDate(suggestedInsertEdit),
      id: context.generateEditId(suggestedInsertEdit.groupId),
    });
    return surroundTextWithComments([textNode, instructionFieldPath], {
      context,
      textRun: insertedTextRun,
    });
  }
  if (suggestedDeleteEdit) {
    const deletedTextRun = new DeletedTextRun({
      ...runProperties,
      author: context.getAuthorName(suggestedDeleteEdit.authorId),
      children: getInlineInstructionFieldChildren(instructionField),
      date: getEditDate(suggestedDeleteEdit),
      id: context.generateEditId(suggestedDeleteEdit.groupId),
    });
    return surroundTextWithComments([textNode, instructionFieldPath], {
      context,
      textRun: deletedTextRun,
    });
  }

  const textRun = new TextRun({
    ...runProperties,
    children: getInlineInstructionFieldChildren(instructionField),
  });

  return surroundTextWithComments([textNode, instructionFieldPath], {
    context,
    textRun,
  });
}
